/**
 * Página de login e registro de usuários
 * Suporta autenticação por email/senha e Google
 * Alterna entre modo login e registro
 */
import { LitElement, html } from 'lit'
import { authService } from '../core/services/auth.service.js'

export class LoginPage extends LitElement {
  static properties = {
    isLogin: { type: Boolean },
    loading: { type: Boolean },
    error: { type: String },
    isSuccess: { type: Boolean },
    showPassword: { type: Boolean },
    showConfirmPassword: { type: Boolean }
  }

  createRenderRoot() {
    return this // manter estilos do Bulma
  }

  constructor() {
    super()
    this.isLogin = true
    this.loading = false
    this.error = ''
    this.isSuccess = false
    this.showPassword = false
    this.showConfirmPassword = false
  }

  connectedCallback() {
    super.connectedCallback()
    
    // Redirecionar se já estiver logado
    if (authService.isAuthenticated()) {
      this._redirectToDashboard()
    }
  }

  _toggleMode() {
    this.isLogin = !this.isLogin
    this.error = ''
    this.isSuccess = false
  }

  async _handleEmailAuth(e) {
    e.preventDefault()
    this.loading = true
    this.error = ''

    const formData = new FormData(e.target)
    const email = formData.get('email')
    const password = formData.get('password')
    const confirmPassword = formData.get('confirmPassword')

    // Validação de confirmação de senha para registro
    if (!this.isLogin) {
      if (password !== confirmPassword) {
        this.error = 'As senhas não coincidem'
        this.loading = false
        return
      }
      if (password.length < 6) {
        this.error = 'A senha deve ter pelo menos 6 caracteres'
        this.loading = false
        return
      }
    }

    try {
      let result
      if (this.isLogin) {
        result = await authService.loginWithEmail(email, password)
        if (result.success) {
          this._redirectToDashboard()
        } else {
          this.error = result.error
        }
      } else {
        result = await authService.registerWithEmail(email, password)
        if (result.success) {
          this.isSuccess = true
          this.error = 'Conta criada! Verifique seu email antes de fazer login.'
          this.isLogin = true
        } else {
          this.error = result.error
        }
      }
    } catch (error) {
      this.error = error.message || 'Erro inesperado. Tente novamente.'
    } finally {
      this.loading = false
    }
  }

  async _handleGoogleAuth() {
    this.loading = true
    this.error = ''

    try {
      const result = await authService.loginWithGoogle()
      if (result.success) {
        this._redirectToDashboard()
      } else {
        this.error = result.error
      }
    } catch (error) {
      this.error = 'Erro ao fazer login com Google.'
    } finally {
      this.loading = false
    }
  }

  _redirectToDashboard() {
    window.location.hash = '#dashboard'
  }

  _togglePasswordVisibility() {
    this.showPassword = !this.showPassword
  }

  _toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword
  }

  render() {
    return html`
      <section class="hero is-fullheight">
        <div class="hero-body">
          <div class="container">
            <div class="columns is-centered">
              <div class="column is-4-desktop is-6-tablet">
                <div class="card">
                  <header class="card-header">
                    <div class="card-header-title is-centered is-flex-direction-column" data-cy="login-header">
                      <img src="/assets/images/logo/logo-text-trim.png" alt="EquiLibra" height="40">
                    </div>
                  </header>
                  
                  <div class="card-content">
                    ${this.error ? html`
                      <div class="notification ${this.isSuccess ? 'is-success' : 'is-danger'} is-light" data-cy="${this.isSuccess ? 'success-message' : 'error-message'}">
                        ${this.error}
                      </div>
                    ` : ''}

                    <form @submit="${this._handleEmailAuth}">
                      <div class="field">
                        <label class="label">Email</label>
                        <div class="control has-icons-left">
                          <input 
                            class="input" 
                            type="email" 
                            name="email"
                            placeholder="seu@email.com"
                            required
                            data-cy="input-email"
                          />
                          <span class="icon is-small is-left">
                            <i class="fas fa-envelope"></i>
                          </span>
                        </div>
                      </div>

                      <div class="field">
                        <label class="label">Senha</label>
                        <div class="control has-icons-left has-icons-right">
                          <input 
                            class="input" 
                            type="${this.showPassword ? 'text' : 'password'}" 
                            name="password"
                            placeholder="Sua senha"
                            required
                            data-cy="input-password"
                          />
                          <span class="icon is-small is-left">
                            <i class="fas fa-lock"></i>
                          </span>
                          <span class="icon is-small is-right is-clickable" @click="${this._togglePasswordVisibility}">
                            <i class="fas ${this.showPassword ? 'fa-eye-slash' : 'fa-eye'}"></i>
                          </span>
                        </div>
                      </div>

                      ${!this.isLogin ? html`
                        <div class="field">
                          <label class="label">Confirmar Senha</label>
                          <div class="control has-icons-left has-icons-right">
                            <input 
                              class="input" 
                              type="${this.showConfirmPassword ? 'text' : 'password'}" 
                              name="confirmPassword"
                              placeholder="Confirme sua senha"
                              required
                              data-cy="input-confirm-password"
                            />
                            <span class="icon is-small is-left">
                              <i class="fas fa-lock"></i>
                            </span>
                            <span class="icon is-small is-right is-clickable" @click="${this._toggleConfirmPasswordVisibility}">
                              <i class="fas ${this.showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}"></i>
                            </span>
                          </div>
                        </div>
                      ` : ''}

                      <div class="field">
                        <div class="control">
                          <button 
                            class="button is-primary is-fullwidth ${this.loading ? 'is-loading' : ''}"
                            type="submit"
                            ?disabled="${this.loading}"
                            data-cy="btn-submit"
                          >
                            ${this.isLogin ? 'Entrar' : 'Criar Conta'}
                          </button>
                        </div>
                      </div>
                    </form>

                    <div class="has-text-centered mb-4">
                      <span class="has-text-grey">ou</span>
                    </div>

                    <div class="field">
                      <div class="control">
                        <button 
                          class="button is-fullwidth ${this.loading ? 'is-loading' : ''}"
                          type="button"
                          ?disabled="${this.loading}"
                          data-cy="btn-google"
                          @click="${this._handleGoogleAuth}"
                        >
                          <span class="icon">
                            <i class="fab fa-google"></i>
                          </span>
                          <span>Continuar com Google</span>
                        </button>
                      </div>
                    </div>

                    <div class="has-text-centered mt-4">
                      <button 
                        class="button is-text"
                        type="button"
                        data-cy="btn-toggle-mode"
                        @click="${this._toggleMode}"
                      >
                        ${this.isLogin ? 'Não tem conta? Criar uma' : 'Já tem conta? Entrar'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    `
  }
}

customElements.define('login-page', LoginPage)